import { getCurrentLoggedInUser } from '@/features/auth/helpers';
import { creditCardType } from '../types';

export const testServers: string[] = [
  'test.secureship.ca',
  'localhost',
];

export const pageWithoutNavNames = [
  'terms',
  'set-password',
  'verify-user',
  'logout',
  'sign-in-confirm-email',
  'account-disabled',
  'account-deleted',
];

export const pageWithoutHeader = [
  'dashboard',
  'shipment-print',
  'shipment-return',
  'query-string',
  'query-string-estimate',
  'query-string-shipment',
  'pickup-schedule',
  'pickup-schedule-confirm',
  'dropoff-requested-confirm',
  'pickup-history',
  'cutoff-times',
  'change-password',
  'api-keys',
  'carrier-accounts',
  'carrier-accounts-add',
  'carrier-accounts-edit',
  'ecommerce',
  'account-users',
  'account-invoices',
  'payment-methods',
  'history-results',
  'shipment-success',
  'verify-password-reset',
  'features-simulation',
];

export const pageWithScrollToTop = [
  'history-results',
  'pickups',
  'pickup-schedule',
  'pickup-history',
  'query-string',
  'account-users',
  'features-simulation',
  'account-invoices',
  'payment-methods',
];

export const exceptionsAndPagesToRedirect: {
  exception: string,
  page: string,
  external?: boolean,
  stopPropagation?: () => boolean,
}[] = [
  {
    exception: 'MustChangePasswordException',
    page: '/account/set-password',
  },
  {
    exception: 'TermsOfConditionsException',
    page: '/terms',
  },
  {
    exception: 'NoPermissionsToAccessOrModifyException',
    page: '/logout',
  },
  {
    exception: 'CurrentUserNotFoundException',
    page: '/logout',
  },
  {
    exception: 'HibpException',
    page: '/account/set-password?type=hibp',
    stopPropagation: () => {
      const isPasswordPages = [
        '/account/change-password',
        '/user/password',
        '/user/verify-user',
      ].some((path) => window.location.pathname.includes(path));

      if (isPasswordPages) return true;
      return false;
    },
  },
  {
    exception: 'UnusualLoginException',
    page: `/sign-in/confirm-email?codeType=UnusualLogin&email=${encodeURIComponent(getCurrentLoggedInUser()?.email || '')}`,
    external: true,
  },
  {
    exception: 'TwoFAException',
    page: `/sign-in/confirm-email?codeType=TwoFA&email=${encodeURIComponent(getCurrentLoggedInUser()?.email || '')}`,
    external: true,
  },
];

export enum AppTheme {
  Default = 'default',
  Ics = 'ics',
  Ics2 = 'ics2',
  Puro = 'puro',
  Puro2 = 'puro2',
  Gls = 'gls',
  Gls2 = 'gls2',
  Gps = 'gps',
  Loomis = 'loomis',
  Loomis2 = 'loomis2'
}

export const appThemeByDomain = {
  secureship: AppTheme.Default,
  test: AppTheme.Default,
  localhost: AppTheme.Default,
  ics: AppTheme.Ics,
  ics2: AppTheme.Ics2,
  puro: AppTheme.Puro,
  puro2: AppTheme.Puro2,
  gls: AppTheme.Gls,
  gls2: AppTheme.Gls2,
  gps: AppTheme.Gps,
  loomis: AppTheme.Loomis,
  loomis2: AppTheme.Loomis2,
};

export const trackPageTypes = {
  // pages
  dashboard: 'Dashboard',
  // shipment routes
  'shipment-create': 'CreateShipment',
  'shipment-results': 'ShipPrices',
  'shipment-customs': 'CommercialInvoice',
  'shipment-checkout': 'CreditCard',
  'shipment-verify': 'Verify',
  'shipment-print': 'Print',
  'shipment-success': 'ShipForSuccess',
  // estimate routes
  'estimate-get-estimate': 'Estimate',
  'estimate-results': 'EstimatePrices',
  // shipment history routes
  'history-results': 'History',
  'history-details': 'Other',
  // pickups routes
  'pickup-schedule': 'SchedulePickup',
  'pickup-schedule-confirm': 'Other',
  'dropoff-requested-confirm': 'Other',
  'pickup-history': 'PickupHistory',
  'cutoff-times': 'CutoffTimes',
  // connect routes
  'api-keys': 'ApiKeys',
  'carrier-accounts': 'CarrierAccounts',
  'carrier-accounts-add': 'Other',
  'carrier-accounts-edit': 'Other',
  ecommerce: 'MarketPlaces',
  'query-string': 'QueryString',
  'query-string-estimate': 'Other',
  'query-string-shipment': 'Other',
  // settings routes
  'settings-preferences': 'Preferences',
  'settings-notifications': 'Notifications',
  // account routes
  'account-users': 'UserManagement',
  'account-invoices': 'Invoices',
  'payment-methods': 'Other',
  'change-password': 'ChangePassword',
  'set-password': 'SetPassword',
  // other routes
  'sign-in': 'Other',
  logout: 'Other',
  terms: 'Other',
  'verify-user': 'Other',
  'verify-password': 'Other',
  'verify-password-reset': 'Other',
  'market-places': 'MarketPlaces',
  // not implemented yet
  'database-connect': 'DatabaseConnect',
  'import-address': 'ImportAddress',
  prices: 'Prices',
  // others
  other: 'Other',
} as const;

export const supportedCreditCards: {
  name: creditCardType,
  length: string,
  prefixes: string,
  checkDigit: boolean,
}[] = [
  {
    name: 'Visa',
    length: '13,16',
    prefixes: '4',
    checkDigit: true,
  },
  {
    name: 'MasterCard',
    length: '16',
    prefixes: '51,52,53,54,55',
    checkDigit: true,
  },
  {
    name: 'AmericanExpress',
    length: '15',
    prefixes: '34,37',
    checkDigit: true,
  },
];
